import { FormEvent, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { Badge, Button, Card, PageHeader } from "@/components/primitives";
import { ConsultantRecommendationResponse, useCosmosClient } from "@/lib/cosmos-api";

export function JourneyNewPage() {
  const search = useSearch({ strict: false }) as { prompt?: string };
  const cosmos = useCosmosClient();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(search.prompt ?? "");
  const [recommendation, setRecommendation] =
    useState<ConsultantRecommendationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draft = async (event: FormEvent) => {
    event.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await new Promise<void>((resolve, reject) => {
        void cosmos.streamRecommendContent(prompt, 12, {
          onDelta: () => {
            setLoading(false);
          },
          onDone: (response) => {
            setRecommendation(response);
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        }).catch(reject);
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to draft journey");
    } finally {
      setLoading(false);
    }
  };

  const requestApproval = async () => {
    if (!recommendation) return;
    const state = await cosmos.requestAction({
      actionType: "journey.assignment.create",
      payload: {
        recommendationId: recommendation.recommendationId,
        blueprint: recommendation.journeyBlueprint
      },
      reason: `Journey drafted from Cosmos prompt: ${prompt}`
    });
    const drafts = JSON.parse(
      window.localStorage.getItem("cosmos:journeys") ?? "[]"
    ) as Array<{ id: string; title: string; summary: string }>;
    window.localStorage.setItem(
      "cosmos:journeys",
      JSON.stringify([
        {
          id: state.requestId,
          title: recommendation.journeyBlueprint.title,
          summary: recommendation.journeyBlueprint.summary
        },
        ...drafts
      ])
    );
    await navigate({ to: "/actions/$requestId", params: { requestId: state.requestId } });
  };

  return (
    <div className="page">
      <PageHeader
        eyebrow="Journey command"
        title="Create a guarded journey request"
        subtitle="Cosmos drafts the blueprint; the write action goes through approval."
      />
      <Card>
        <form className="prompt-form" onSubmit={(event) => void draft(event)}>
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Describe the audience, skills, duration, and constraints."
          />
          <Button disabled={loading || !prompt.trim()}>
            {loading && <Loader2 className="spin" size={16} />}
            Draft journey
          </Button>
        </form>
      </Card>
      {error && <Card className="error-card">{error}</Card>}
      {recommendation && (
        <Card>
          <div className="card-header">
            <h2>{recommendation.journeyBlueprint.title}</h2>
            <Badge tone="ai">{recommendation.recommendationId.slice(0, 8)}</Badge>
          </div>
          <p>{recommendation.journeyBlueprint.summary}</p>
          <ol>
            {recommendation.journeyBlueprint.steps.map((step, index) => (
              <li key={`${step.title}-${index}`}>
                <strong>{step.title}</strong>
                {step.durationMinutes ? ` (${step.durationMinutes} min)` : ""} — {step.why}
              </li>
            ))}
          </ol>
          <Button onClick={() => void requestApproval()}>Request approval</Button>
        </Card>
      )}
    </div>
  );
}
